<template>
  <div id="cesium-container" ref="cesiumContainer">
    <div id="blurred-background" v-if="isViewerLoading || loading">
      <v-progress-circular
        id="loading-indicator"
        :size="40"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
    <cesium-recorder ref="cesiumRecorder" />
  </div>
</template>

<script>
import { CesiumInitialization } from '../../components/ThreeD/CesiumInitialization'
import { AnalyticalOrbit } from '../../components/ThreeD/AnalyticalOrbit'
import { PointOfInterest } from '../../components/ThreeD/PointOfInterest'
import CesiumRecorder from '../../components/ThreeD/CesiumRecorder.vue'
import CesiumTools from '../../components/ThreeD/CesiumTools'
import * as Cesium from 'cesium'

export default {
  name: 'analyticalOrbit-cesium-viewer',
  mixins: [CesiumInitialization, AnalyticalOrbit, PointOfInterest],
  props: {
    tracesResults: Array,
    loading: Boolean,
    offline: {
      type: Boolean,
      default: false
    }
  },
  components: {
    'cesium-recorder': CesiumRecorder
  },
  data() {
    return {
      isViewerLoading: false,
      colorsArray: [],
      labelCollection: null,
      orbitsPC: null
    }
  },
  watch: {
    tracesResults: {
      handler(geometries) {
        this.updateTraces(geometries)
      },
      deep: true
    }
  },

  mounted() {
    this.isViewerLoading = true
    let cesiumContainer = this.$refs.cesiumContainer
    this.initializeViewer(false, false, true, true, false, this.offline)
    this.insertElementInToolbar(this.$refs.cesiumRecorder.$el)
    this.hideSceneModePicker()
    this.orbitsPC = new Cesium.PrimitiveCollection()
    this.orbitTracesPC = new Cesium.PolylineCollection()
    this.labelCollection = new Cesium.LabelCollection()
    this.orbitsPC.add(this.orbitTracesPC)
    this.orbitsPC.add(this.labelCollection)
    this.viewer.scene.primitives.add(this.orbitsPC)
    this.updateTraces(this.tracesResults)
    this.isViewerLoading = false
  },

  methods: {
    updateTraces(geometries) {
      if (!geometries || !geometries.length) {
        return
      }
      this.isViewerLoading = true
      CesiumTools.removePrimitivesFromPC(this.orbitTracesPC)
      CesiumTools.removePrimitivesFromPC(this.labelCollection)
      geometries.forEach((geometry) => {
        let tracesResults =
          geometry.length > 1
            ? [...geometry].sort((a, b) => (a.altitude < b.altitude ? -1 : 1))
            : geometry
        this.colorsArray = CesiumTools.createColorsArray(geometry.length)
        if (!tracesResults || !tracesResults.length) {
          return
        }
        tracesResults.forEach((traceData, index) => {
          const altitude = traceData.altitude / 1e3
          const inclination = Math.round(
            CesiumTools.toDegrees(traceData.inclination)
          )
          const labelPos = Cesium.Cartesian3.unpack(
            traceData.traces[0][10 * index]
          )
          const color = this.colorsArray[index]
          const polylineMaterial = this.createPolylineMaterial(false, color, 1)
          this.addOneAnalyticalTrace(
            this.orbitTracesPC,
            [].concat.apply([], traceData.traces[0]),
            polylineMaterial,
            true
          )
          let label = traceData.label
            ? traceData.label
            : `${altitude} km - ${inclination}°`

          this.labelCollection.add({
            position: labelPos,
            text: label,
            pixelOffset: new Cesium.Cartesian2(0, 0),
            font: '10px sans-serif',
            scaleByDistance: new Cesium.NearFarScalar(
              1.5e2,
              1.5,
              Infinity,
              0.5
            ),
            showBackground: false,
            // backgroundColor: new Cesium.Color(0, 0, 0, 1),
            // eye-offset is used to make it appears above other primitives
            eyeOffset: new Cesium.Cartesian3(0, 0, -1e6),
            horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            outlineColor: color
          })
        })
      })

      this.isViewerLoading = false
    }
  }
}
</script>

<style scoped>
#cesium-container {
  position: relative;
  height: 500px;
  width: 100%;
  z-index: 2;
}

#blurred-background {
  background-color: black;
  opacity: 0.5;
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: 3;
}

#loading-indicator {
  position: absolute;
  margin: auto;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 4;
  display: block;
}
</style>
